<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column is-one-third">
            <nav class="panel">
              <h2 class="panel-heading">
                Edit Screens & Menus
              </h2>
              <template v-for="(section, index) in sections">
                <a
                  :key="`${section.label}-${index}`"
                  class="panel-block"
                  :class="activeSection === section.label ? 'active' : ''"
                  @click="activeSection = section.label"
                >
                  <span
                    class="panel-icon"
                  >
                    <i
                      class="fa-window"
                      :class="activeSection === section.label ? 'fad' : 'fal'"
                      aria-hidden="true"
                    />
                  </span>
                  <span :class="activeSection === section.label ? 'has-text-primary' : ''">
                    {{ section.label }}
                  </span>
                </a>
                <template v-for="(item, index2) in section.items">
                  <div
                    v-show="activeSection === section.label"
                    :key="`${item.code}-${index2}`"
                    class="section-items"
                  >
                    <a
                      :key="section.label"
                      class="panel-blocks"
                      :class="item.code === currentCode.code ? 'current' : ''"
                      @click.prevent="getControlValueCode(item)"
                    >
                      <span class="panel-icon">
                        <i
                          class="fa"
                          :class="item.code === currentCode.code ? 'fa-chevron-double-right' : 'fa-chevron-right'"
                          aria-hidden="true"
                        />
                      </span>
                      {{ item.label }}
                    </a>
                  </div>
                </template>
              </template>
            </nav>
          </div>
          <div class="column is-two-thirds">
            <template v-if="Object.keys(currentCode).length === 0">
              <div class="message is-info">
                <div class="message-body">
                  <i>First, select a section on the left side.</i>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="columns is-multiline">
                <div class="column is-12">
                  <div class="message is-warning">
                    <div class="message-body">
                      Avoid changing or deleting existing items' values that are already in use. Instead, it's best to add a new item and make the unwanted item inactive.
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <h3 class="title is-5">
                    {{ currentCode.label }}
                  </h3>
                  <div class="subtitle is-5">
                    Drag row to reorder the list item
                  </div>
                </div>
                <div class="column is-6 has-text-right">
                  <button
                    id="menu-add-new-btn"
                    type="button"
                    class="button is-secondary"
                    @click="addNewItem"
                  >
                    Add New {{ currentCode.label }} &nbsp; <i class="fa fa-plus" />
                  </button>
                </div>
                <div class="column is-12">
                  <div class="table-responsive">
                    <table class="table is-bordered is-striped is-fullwidth table-content-center">
                      <thead>
                        <tr>
                          <td class="draggable-handle" />
                          <th>Description</th>
                          <th>Value</th>
                          <th class="has-text-centered">
                            Active
                          </th>
                          <th class="has-text-centered">
                            Edit
                          </th>
                          <th class="has-text-centered">
                            Delete
                          </th>
                        </tr>
                      </thead>
                      <draggable
                        v-model="items"
                        tag="tbody"
                        handle=".handle"
                        @end="updateSequenceNo"
                      >
                        <tr
                          v-for="(item, index) in items"
                          :key="`${item.id}-${index}`"
                          :class="getItemClass(item.cud)"
                        >
                          <td class="draggable-handle">
                            <i class="fal fa-align-justify handle" />
                          </td>
                          <td>{{ item.description }}</td>
                          <td>{{ item.value }}</td>
                          <td class="has-text-centered">
                            {{ item.active === 'Y' ? 'Yes' : 'No' }}
                          </td>
                          <td class="has-text-centered">
                            <a @click.prevent="editElement(item)"><i class="fas fa-edit" /></a>
                          </td>
                          <td class="has-text-centered">
                            <a @click.prevent="removeItemModal(item)"><i class="fas fa-trash" /></a>
                          </td>
                        </tr>
                      </draggable>
                      <tfoot>
                        <tr>
                          <td
                            class="is-size-7"
                            colspan="6"
                          >
                            <span class="icon has-text-warning">
                              <i class="fad fa-square" />
                            </span>items to be deleted &nbsp;
                            <span class="icon has-text-info">
                              <i class="fad fa-square" />
                            </span>items to be added <br>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div class="columns">
                      <div class="column has-text-centered">
                        <button
                          id="save-btn"
                          type="button"
                          class="button is-primary"
                          @click="updateList"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <modal
          name="item-add-edit-modal"
          height="auto"
          :scrollable="true"
          :dynamic="true"
          :adaptive="true"
          :classes="[ 'is-small' ]"
          @beforeOpen="beforeLoadingModal"
        >
          <div class="modal is-active">
            <validation-observer
              ref="item-form"
              tag="form"
              class="no-margin"
            >
              <div class="modal-background" />
              <div class="modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">
                    {{ action === 'add' ? 'Adding' : 'Editing: ' }} {{ currentCode.label }}
                  </p>
                  <button
                    id="modal-close-btn"
                    type="button"
                    class="delete"
                    aria-label="close"
                    @click.prevent="closeModal('item-add-edit-modal')"
                  />
                </header>
                <section class="modal-card-body">
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <textbox-vee
                        id="description"
                        v-model="currentItem.description"
                        name="description"
                        rules="required"
                        placeholder="Description / Label"
                      />
                    </div>
                    <div class="column is-12">
                      <textbox-vee
                        id="value"
                        v-model="currentItem.value"
                        name="value"
                        rules="required"
                        placeholder="Value"
                      />
                    </div>
                    <div class="column is-12 has-text-centered">
                      <checkbox-adv
                        v-model="currentItem.active"
                        value="Y"
                        true-value="Y"
                        false-value="N"
                      >
                        Check to make it active
                      </checkbox-adv>
                    </div>
                  </div>
                </section>
                <footer class="modal-card-foot">
                  <input
                    v-model="currentItem.id"
                    type="hidden"
                  >
                  <button
                    id="add-edit-btn"
                    type="button"
                    class="button is-primary"
                    @click="addEditDropDownList"
                  >
                    {{ action === 'add' ? 'Add Item' : 'Update Item' }}
                  </button>
                </footer>
              </div>
            </validation-observer>
          </div>
        </modal>
        <modal
          name="item-delete-modal"
          height="auto"
          :scrollable="true"
          :dynamic="true"
          :adaptive="true"
          :classes="[ 'is-small' ]"
        >
          <div class="modal is-active">
            <validation-observer
              ref="item-form"
              tag="form"
              class="no-margin"
            >
              <div class="modal-background" />
              <div class="modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">
                    Delete Item
                  </p>
                  <button
                    id="modal-delete-btn"
                    type="button"
                    class="delete"
                    aria-label="close"
                    @click.prevent="closeModal('item-delete-modal')"
                  />
                </header>
                <section class="modal-card-body">
                  <div class="columns is-multiline">
                    <div class="column is-12">
                      <p>
                        Only delete items that are absolutely not needed. If you are not sure this item is being used by the sytem, make it inactive, instead of deleting it. Are you sure you want to delete* the item <strong>{{ currentItem.description }}</strong>?
                      </p>
                    </div>
                  </div>
                </section>
                <footer class="modal-card-foot">
                  <input
                    v-model="currentItem.id"
                    type="hidden"
                  >
                  <button
                    id="modal-remove-btn"
                    type="button"
                    class="button is-danger"
                    @click="removeItem"
                  >
                    Delete
                  </button>
                  <p class="is-size-7">
                    *This operation may fail if the item being deleted is currently being used by the system.
                  </p>
                </footer>
              </div>
            </validation-observer>
          </div>
        </modal>
      </div>
    </div>
  </div>
</template>
<script>
import { v1 as uuid } from 'uuid';
import draggable from "vuedraggable";
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import { Textbox } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';
import Vue from 'vue';

const TextboxVee = withValidation(Textbox);


function getCleanCurrentItem() {
  return {
    id: uuid(),
    value: '',
    description: '',
    active: 'N',
  };
}

export default {
  components: {
    draggable,
    CheckboxAdv,
    TextboxVee,
  },
  data() {
    return {
      items: [],
      currentCode: {},
      currentItem: getCleanCurrentItem(),
      dragging: false,
      action: '',
      activeSection: '',
      sections: [
        {
          label: 'Screen: Income Sources',
          items: [
            {
              code: 'incometype',
              label: 'Income Types',
            },
            // {
            //   code: 'incamount',
            //   label: 'Income Amount',
            // },
            {
              code: 'incomeSourceText',
              label: 'Screen Text',
            },
            {
              code: 'incomeSourceTooltip',
              label: 'Tooltips Text',
            },
          ],
        },
        {
          label: 'Screen: Creditors',
          items: [
            {
              code: 'loanamount',
              label: 'Loan Amounts',
            },
            {
              code: 'creditorsText',
              label: 'Screen Text',
            },
            {
              code: 'creditorsTooltip',
              label: 'Tooltips Text',
            },
          ],
        },
        {
          label: 'Screen: Real Estate',
          items: [
            {
              code: 'relocation',
              label: 'Real Estate Locations',
            },
            {
              code: 'realEstateText',
              label: 'Screen Text',
            },
            {
              code: 'realEstateTooltip',
              label: 'Tooltips Text',
            },
          ],
        },
        {
          label: 'Screen: Gifts',
          items: [
            {
              code: 'gifts',
              label: 'Gift Types',
            },
            {
              code: 'giftsText',
              label: 'Screen Text',
            },
          ],
        },
        {
          label: 'Screen: Directorships',
          items: [
            {
              code: 'positiontype',
              label: 'Position Types',
            },
            {
              code: 'directorshipText',
              label: 'Screen Text',
            },
            {
              code: 'directorshipTooltip',
              label: 'Tooltips Text',
            },
          ],
        },
        {
          label: 'Screen: Business Interest',
          items: [
            {
              code: 'businessInterestText',
              label: 'Screen Text',
            },
            {
              code: 'businessInterestTooltip',
              label: 'Tooltips Text',
            },
          ],
        },
        {
          label: 'Screen: Bus. Int. Transferred',
          items: [
            {
              code: 'businessIntTransText',
              label: 'Screen Text',
            },
          ],
        },
        {
          label: 'Screen: Family',
          items: [
            {
              code: 'familymember',
              label: 'Family Members',
            },
            {
              code: 'mayorformcert',
              label: 'Mayor Form Certification',
            },
          ],
        },
        {
          label: 'User Positions',
          items: [
            {
              code: 'agency',
              label: 'Agencies / Departments',
            },
          ],
        },
        {
          label: 'Screen: Filing Info',
          items: [
            // {
            //   code: 'filerstatus',
            //   label: 'Filer Statuses 1',
            // },
            {
              code: 'status',
              label: 'Filer Statuses',
            },
            {
              code: 'filingyears',
              label: 'Filing Years',
            },
            {
              code: 'forms',
              label: 'Filing Forms',
            },
            {
              code: 'currentyear',
              label: 'Current Filing Year',
            },
            {
              code: 'filingduedate',
              label: 'Filing Due Date',
            },
            {
              code: 'filingstatus',
              label: 'Filing Status',
            },
          ],
        },
        {
          label: 'Other',
          items: [
            {
              code: 'state',
              label: 'States',
            },
            {
              code: 'county',
              label: 'PA Counties',
            },
            {
              code: 'disclaimer',
              label: 'Disclaimer',
            },
            {
              code: 'MAINTENANCEMODE',
              label: 'Maintenance Mode',
            },
            {
              code: 'esignature',
              label: 'eSignature',
            },
            {
              code: 'PUBLICSEARCHYEARS',
              label: 'Public Search Years',
            },
          ],
        },
        {
          label: 'Contact Info',
          items: [
            {
              code: 'fdemail',
              label: 'FD E-mail',
            },
            {
              code: 'fdsite',
              label: 'FD Site',
            },
            {
              code: 'recordsaddress1',
              label: 'Records Address 1',
            },
            {
              code: 'recordsaddress2',
              label: 'Records Address 2',
            },
            {
              code: 'recordscity',
              label: 'Records City',
            },
            {
              code: 'recordsphone',
              label: 'Records Phone',
            },
            {
              code: 'recordstitle',
              label: 'Records Title',
            },
          ],
        },
        // {
        //   label: 'DEPRECATED?',
        //   items: [
        // {
        //   code: 'emailpasswdtemplate',
        //   label: 'E-mail Password Template',
        // },
        // {
        //   code: 'emailchgpasswdtemplate',
        //   label: 'E-mail Change Password Template',
        // },
        // {
        //   code: 'emailuseridtemplate',
        //   label: 'E-mail User ID Template',
        // },
        // {
        //   code: 'disclaimer',
        //   label: 'Disclaimer',
        // },
        // {
        //   code: 'filersetupmatchtype',
        //   label: 'Filer Setup Match Type',
        // },

        // {
        //   code: 'transmitfile',
        //   label: 'Transmit File',
        // },
        // {
        //   code: 'usertype',
        //   label: 'User Type',
        // },
        // ],
        // },
      ],
      menus: [],
    };
  },
  created () {
    this.$store.commit('loading', false);
  },
  methods: {
    async getControlValueCode (item) {
      await this.$store.dispatch('admin/getControlValue', item.code);
      Vue.set(this, 'items', this.cloneObj(this.$store.state.admin.controlValues[item.code]));
      this.currentCode = item;
    },
    getItemIndex (item) {
      const index = this.items.findIndex(element => {
        return element.id === item.id;
      });
      return index;
    },
    updateSequenceNo () {
      this.items.forEach((item, index) => {
        Vue.set(item, 'sequenceNo', index);
        Vue.set(item, 'cud', 'put');
      });
    },
    removeItemModal (item) {
      this.currentItem = item;
      this.$modal.show('item-delete-modal');
    },
    removeItem () {
      const index = this.getItemIndex(this.currentItem);
      if (this.items[index]['cud'] && this.items[index]['cud'] === 'post') {
        this.items.splice(index, 1);
      } else {
        Vue.set(this.items[index], 'cud', 'delete');
      }
      this.closeModal('item-delete-modal');
    },
    closeModal (modal) {
      this.currentItem = getCleanCurrentItem();
      this.$modal.hide(modal);
    },
    beforeLoadingModal () {
      this.$refs['item-form'].reset();
    },
    editElement (item) {
      this.action = 'edit';
      this.currentItem = { ...getCleanCurrentItem(), ...item };
      this.$modal.show('item-add-edit-modal');
    },
    addNewItem () {
      this.action = 'add',
      this.$modal.show('item-add-edit-modal');
    },
    getItemClass (cud) {
      if (cud) {
        if (cud === 'delete') {
          return 'has-background-warning';
        }
        if (cud === 'post') {
          return 'has-background-info';
        }
      }
      return '';
    },
    async addEditDropDownList() {
      const isValid = await this.$refs['item-form'].validate();

      if (!isValid) {
        return false;
      }

      if (this.action === 'edit') {
        const index = this.getItemIndex(this.currentItem);

        if (index !== -1) {
          this.items[index] = this.currentItem;
          if (!this.items[index]['cud']) {
            Vue.set(this.items[index], 'cud', 'put');
          }
        }
      } else {
        this.currentItem['cud'] = 'post';
        this.currentItem['code'] = this.currentCode.code;
        this.currentItem['sequenceNo'] = this.items.length;
        this.items.push(this.currentItem);
      }

      this.closeModal('item-add-edit-modal');
    },
    updateList() {
      if (this.items) {
        this.$store.dispatch('admin/updateControlValue', this.items).then(() => {
          this.$success(`${this.currentCode.label} updated.`);
          this.getControlValueCode(this.currentCode);
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
  table {
    tr, td {
      vertical-align: middle;
    }
    tr {
      &.to-be-deleted {
        td {
          background-color: #ffcdcd;
        }
      }
    }
    .draggable-handle {
      width: 35px;
      font-size: 22px;
      padding: 0 10px;
      color: $ben-franklin-blue;
      cursor: pointer;
    }
    .sortable-choosen, .sortable-ghost {
      background-color: #0f4d90;
      color: #fff;
      .draggable-handle {
        color: #fff;
      }
      td {
        border: none;

        border-top: 3px dashed #DAEDFE;
        border-bottom: 3px dashed #DAEDFE;

        &:last-child {
          border-right: 3px dashed #DAEDFE;
        }
        &:first-child {
          border-left: 3px dashed #DAEDFE;
        }
      }
    }
  }
  .section-items {
    a {
      margin-left: 2.2rem;
      padding:.5rem .5rem .5rem 0;
      border-bottom: 1px solid $ghost-grey;
      display: block;
      .panel-icon {
        line-height: 1.5rem;
      }
    }
  }
  .panel-block {
    &.active {
      border-bottom: 0;
    }
  }
</style>

